'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Link } from '@/i18n/navigation';
import { HelpCircle } from 'lucide-react';

interface AuthButtonsProps {
  isMobile?: boolean;
  isAuthenticated: boolean;
  setIsAuthModalOpen: (isOpen: boolean) => void;
  closeMenu: () => void;
  linkTarget?: string;
  openItem?: string;
  onOpenItemChange?: (value: string | undefined) => void;
}

export const AuthButtons = ({
  isMobile = false,
  isAuthenticated,
  setIsAuthModalOpen,
  closeMenu,
  linkTarget,
  openItem,
  onOpenItemChange,
}: AuthButtonsProps) => {
  if (isAuthenticated) return null;

  return (
    <>
      {isMobile ? (
        <>
          <li className="!mb-0 w-full">
            <Accordion
              type="single"
              collapsible
              className="w-full"
              value={openItem}
              onValueChange={onOpenItemChange}
            >
              <AccordionItem value="help" className="border-b-0">
                <div className="flex h-16 w-full items-center justify-between gap-3 border-gray-200 border-b px-4 py-0">
                  <button
                    aria-label="Log in to your account"
                    onClick={() => {
                      setIsAuthModalOpen(true);
                      closeMenu();
                    }}
                    className="cursor-pointer rounded-lg border border-gray-400 px-4 py-2 font-bold text-lg tracking-tight antialiased transition-colors hover:text-primary"
                  >
                    Login
                  </button>
                  <button
                    aria-label="Sign up for free"
                    onClick={() => {
                      setIsAuthModalOpen(true);
                      closeMenu();
                    }}
                    className="btn-purple flex h-12 cursor-pointer items-center justify-center whitespace-nowrap rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] px-6 font-bold text-base text-white tracking-tight antialiased shadow-md transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Sign up for free
                  </button>

                  <AccordionTrigger aria-label="Help menu" className="w-auto flex-none cursor-pointer border-0 p-0 hover:bg-transparent hover:no-underline">
                    <div>
                      <HelpCircle className="h-6 w-6 text-gray-600" aria-hidden="true" />
                    </div>
                  </AccordionTrigger>
                </div>

                <AccordionContent className="bg-gray-200 p-0">
                  <ul className="-ml-4 flex w-[calc(100%+2rem)] flex-col gap-4 border-gray-200 border-l-4 bg-gray-200 py-4 pl-16 text-left">
                    <li>
                      <Link
                        href="/pricing"
                        className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                        onClick={closeMenu}
                        target={linkTarget}
                      >
                        <div className="font-bold text-base text-foreground group-hover:text-primary">
                          See Plans & Pricing
                        </div>
                        <div className="text-muted-foreground text-sm">View pricing plans</div>
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/how-it-works"
                        className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                        onClick={closeMenu}
                        target={linkTarget}
                      >
                        <div className="font-bold text-base text-foreground group-hover:text-primary">
                          How it works
                        </div>
                        <div className="text-muted-foreground text-sm">Learn the process</div>
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/help"
                        className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                        onClick={closeMenu}
                        target={linkTarget}
                      >
                        <div className="font-bold text-base text-foreground group-hover:text-primary">
                          FAQs & Help
                        </div>
                        <div className="text-muted-foreground text-sm">Get answers</div>
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/contact"
                        className="group -ml-2 block w-full cursor-pointer rounded-md p-2 transition-colors hover:bg-white"
                        onClick={closeMenu}
                        target={linkTarget}
                      >
                        <div className="font-bold text-base text-foreground group-hover:text-primary">
                          Contact Us
                        </div>
                        <div className="text-muted-foreground text-sm">Get in touch</div>
                      </Link>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </li>
        </>
      ) : (
        <div className="flex h-12 items-center gap-4">
          <button
            aria-label="Log in to your account"
            onClick={() => setIsAuthModalOpen(true)}
            className="nav-link flex h-12 cursor-pointer items-center whitespace-nowrap font-bold tracking-tight antialiased"
          >
            Log in
          </button>
          <button
            aria-label="Sign up for free"
            onClick={() => setIsAuthModalOpen(true)}
            className="btn-purple flex h-12 transform cursor-pointer items-center whitespace-nowrap rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] px-8 font-bold text-[15px] text-white tracking-tight antialiased shadow-md transition-all duration-300 hover:scale-105 active:scale-95"
          >
            <span className="min-[890px]:hidden">Sign up</span>
            <span className="hidden min-[890px]:inline">Sign up for free</span>
          </button>

          {/* Help Dropdown */}
          <NavigationMenu
            viewport={false}
            className="block translate-y-[5px] [&>div.absolute]:right-0 [&>div.absolute]:left-auto"
          >
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  aria-label="Help menu"
                  className="flex h-12 w-12 translate-y-[2px] cursor-pointer items-center justify-center rounded-md p-0 transition-colors hover:bg-gray-50 focus:bg-gray-50 data-[state=open]:bg-gray-50"
                >
                  <HelpCircle className="h-6 w-6 text-gray-600" aria-hidden="true" />
                </NavigationMenuTrigger>
                <NavigationMenuContent className="absolute top-full right-0 left-auto border border-gray-200 bg-white shadow-lg">
                  <ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[1fr_1fr]">
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          href="/pricing"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="font-bold text-base leading-none">
                            See Plans & Pricing
                          </div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            View pricing plans
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          href="/how-it-works"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="font-bold text-base leading-none">How it works</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Learn the process
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          href="/help"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="font-bold text-base leading-none">FAQs & Help</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Get answers
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          href="/contact"
                          target={linkTarget}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="font-bold text-base leading-none">Contact Us</div>
                          <p className="line-clamp-2 text-muted-foreground text-sm leading-snug">
                            Get in touch
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      )}
    </>
  );
};
